<template>
    <info-widget :collapsible="true" :is-expanded="locationExpanded" @toggle_expand="locationExpanded = !locationExpanded">
        <template v-slot:title><span class="text-muted">Location: </span>{{ location.work_location.name }} <span class="font-weight-normal">({{ count }})</span></template>
        <template v-slot:headerbutton>
            <span v-if="locationExpanded" class="changes-expand-all mr-2" role="button" @click.stop.prevent="toggleAllSections">{{ allSectionsExpanded ? 'Collapse All' : 'Expand All' }}</span>
        </template>

        <div class="p-1">
            <template v-if="oneTimeSections.length">
                <h4 class="pl-2 pt-1 pb-0">One-Time Changes</h4>
                <div class="pl-1">
                    <div v-for="section in oneTimeSections" :key="section.key" class="changes-type-section">
                        <a href class="changes-type-header collapse-trigger" role="button" :class="{'is-expanded': isExpanded(section.key)}" @click.prevent="toggle(section.key)">
                            <span class="widget-toggle-wrapper">
                                <collapse-toggle-button />
                            </span>
                            <span>{{ section.title }} ({{ section.rows.length }})</span>
                        </a>
                        <changes-report-table v-if="isExpanded(section.key)" :columns="section.columns" :rows="section.rows" @open-employee="$emit('open-employee', $event)" />
                    </div>
                </div>
            </template>

            <template v-if="permanentSections.length">
                <h4 class="pl-2 pt-2 pb-0">Permanent Changes</h4>
                <div class="pl-1">
                    <div v-for="section in permanentSections" :key="section.key" class="changes-type-section">
                        <a href class="changes-type-header collapse-trigger" role="button" :class="{'is-expanded': isExpanded(section.key)}" @click.prevent="toggle(section.key)">
                            <span class="widget-toggle-wrapper">
                                <collapse-toggle-button />
                            </span>
                            <span>{{ section.title }} ({{ section.rows.length }})</span>
                        </a>
                        <changes-report-table v-if="isExpanded(section.key)" :columns="section.columns" :rows="section.rows" @open-employee="$emit('open-employee', $event)" />
                    </div>
                </div>
            </template>
        </div>
    </info-widget>
</template>

<script>
import InfoWidget from '@/components/InfoWidget'
import ChangesReportTable from './ChangesReportTable'

export default {
    components: {InfoWidget: InfoWidget, ChangesReportTable: ChangesReportTable},
    props: ['location', 'count'],
    emits: ['open-employee'],
    data() {
        return {
            locationExpanded: false,
            expandedKeys: {},
        }
    },
    computed: {
        oneTimeChanges() {
            return this.location.one_time_changes || {}
        },
        permanentChanges() {
            return this.location.permanent_changes || {}
        },
        // Only render type sections that actually have rows, so a location with (say) only a
        // rate change doesn't show three empty One-Time tables.
        oneTimeSections() {
            const sections = [
                {
                    key: 'separate_checks',
                    title: 'Separate Checks Added',
                    rows: this.oneTimeChanges.separate_checks || [],
                    columns: [
                        {label: 'Check Amount', field: 'amount', money: true},
                        {label: 'Added By', field: 'added_by'},
                    ],
                },
                {
                    key: 'blocked_taxes',
                    title: 'Taxes Blocked',
                    rows: this.oneTimeChanges.blocked_taxes || [],
                    columns: [
                        {label: 'Type', field: 'type'},
                        {label: 'Blocked By', field: 'blocked_by'},
                    ],
                },
                {
                    key: 'blocked_deductions',
                    title: 'Deductions Blocked',
                    rows: this.oneTimeChanges.blocked_deductions || [],
                    columns: [
                        {label: 'Type', field: 'type'},
                        {label: 'Blocked By', field: 'blocked_by'},
                    ],
                },
            ]
            return sections.filter(section => section.rows.length)
        },
        permanentSections() {
            const sections = [
                {
                    key: 'rate_changes',
                    title: 'Rate of Pay Changes',
                    rows: this.permanentChanges.rate_changes || [],
                    columns: [
                        {label: 'Position', field: 'position'},
                        {label: 'Earning', field: 'earning'},
                        {label: 'Old Value', field: 'old_value'},
                        {label: 'New Value', field: 'new_value'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Changed By', field: 'changed_by'},
                    ],
                },
                {
                    key: 'positions_added',
                    title: 'Positions Added',
                    rows: this.permanentChanges.positions_added || [],
                    columns: [
                        {label: 'Position', field: 'position'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Added By', field: 'added_by'},
                    ],
                },
                {
                    key: 'positions_removed',
                    title: 'Positions Removed',
                    rows: this.permanentChanges.positions_removed || [],
                    columns: [
                        {label: 'Position', field: 'position'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Removed By', field: 'removed_by'},
                    ],
                },
                {
                    key: 'deduction_changes',
                    title: 'Deduction Changes',
                    rows: this.permanentChanges.deduction_changes || [],
                    columns: [
                        {label: 'Type', field: 'deduction'},
                        {label: 'Value Changed', field: 'value_changed'},
                        {label: 'Old Value', field: 'old_value'},
                        {label: 'New Value', field: 'new_value'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Changed By', field: 'changed_by'},
                    ],
                },
                {
                    key: 'deductions_added',
                    title: 'Deductions Added',
                    rows: this.permanentChanges.deductions_added || [],
                    columns: [
                        {label: 'Deduction', field: 'deduction'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Added By', field: 'added_by'},
                    ],
                },
                {
                    key: 'deductions_removed',
                    title: 'Deductions Removed',
                    rows: this.permanentChanges.deductions_removed || [],
                    columns: [
                        {label: 'Deduction', field: 'deduction'},
                        {label: 'Date', field: 'date', date: true},
                        {label: 'Removed By', field: 'removed_by'},
                    ],
                },
            ]
            return sections.filter(section => section.rows.length)
        },
        sectionKeys() {
            return this.oneTimeSections.concat(this.permanentSections).map(section => section.key)
        },
        allSectionsExpanded() {
            return this.sectionKeys.length > 0 && this.sectionKeys.every(key => this.expandedKeys[key])
        },
    },
    methods: {
        isExpanded(key) {
            return !!this.expandedKeys[key]
        },
        toggle(key) {
            this.expandedKeys[key] = !this.expandedKeys[key]
        },
        toggleAllSections() {
            const expand = !this.allSectionsExpanded
            const next = {}
            this.sectionKeys.forEach(key => { next[key] = expand })
            this.expandedKeys = next
        },
    },
}
</script>

<style scoped lang="scss">
.changes-expand-all {
    color: #6c757d;
    cursor: pointer;
    white-space: nowrap;
    font-size: 0.85rem;
    font-weight: 400;

    &:hover {
        color: #495057;
        text-decoration: underline;
    }
}

.changes-type-header {
    display: flex;
    align-items: center;
    padding: 0.5rem 0.25rem;
    border-bottom: 1px solid #e9ecef;
    color: inherit;
    text-decoration: none;

    &:hover {
        color: inherit;
        text-decoration: none;
    }

    .widget-toggle-wrapper {
        margin-right: 0.5rem;
    }
}
</style>
